import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from './useAppDispatch';
import { fetchGeoFilters } from '../store/slice/filter-slice';
import { GlobalFiltersState, setGlobalFilter } from '../store/slice/global-filters';
import { useLocation } from 'react-router-dom';
import { GeoClass } from '../interface/response/GeoFiltersResponse';
import { CASH_REGISTER_URL } from '../util/url';
import { GeoColumns } from '../enum/GeoDivisionColumnName';
import useDateAndTime from './useDateAndTime';

/**
 * `useGeoFilter` is a custom React hook that manages cascading geo-filters
 * where each filter level depends on the selections made in the previous one.
 *
 * ## Features
 *
 * - **handleChange(level, selectedItem)**
 *   Updates the selected items for the given level.
 *   Clears all lower-level selections.
 *   Triggers loading of new options for the next level.
 *
 * - **handleDropdownOpen(level)**
 *   Lazy-loads options when a dropdown is opened.
 *   Loads data only if the previous level has valid selections.
 *
 * - **selectAll(level, options)**
 *   Selects or deselects all items at the given level.
 *   Automatically triggers loading for the next level when selecting all.
 *
 * - **isDisabled(level)**
 *   Determines whether a filter column should be disabled.
 *
 * ## Disabled Column Logic
 *
 * A column is **disabled** when:
 *
 * - It is not the first column **AND**
 * - The previous column does not have any selected items **OR**
 * - Any parent column up the chain is disabled.
 *
 * Memoization is used to avoid repeated recursive computations.
 *
 * ## Returned API
 *
 * - `handleChange(col: string, option: GeoClass): void`
 * - `handleDropdownOpen(col: string): void`
 * - `selectAll(col: string, options: GeoClass[]): void`
 * - `isDisabled(col: string): boolean`
 * - `numberOfSelected: Record<string, number>`
 * - `filteredGeoData: any`
 * - `onFilteredData: Record<string, (data: GeoClass[]) => void>`
 */

export const useGeoFilter = (
  filters: any,
  setFilters: (f: GeoClass) => void,
  columns: string[],
  allOptsFiltered: Record<string, any[]>,
) => {
  const dispatch = useAppDispatch();
  const [numberOfSelected, setNumberOfSelected] = useState<Record<string, number>>({});
  const [filteredGeoData, setFilteredGeoData] = useState<Record<string, GeoClass[]>>({});
  const { startDate } = useDateAndTime();

  /**
   * Maps each column to a callback that updates filtered geo data.
   * Optimized to avoid unnecessary re-renders.
   */

  const onFilteredData = useMemo(() => {
    const map: Record<string, (filteredData: GeoClass[]) => void> = {};

    columns.forEach((col: any) => {
      map[col] = (filteredData) => {
        setFilteredGeoData((prev: any) => {
          const prevList = prev[col];

          // Fast equality check
          if (
            prevList?.length === filteredData.length &&
            prevList?.every((x: any, i: number) => x.ID === filteredData[i].ID)
          ) {
            return prev; // No changes
          }

          return {
            ...prev,
            [col]: filteredData,
          };
        });
      };
    });

    return map;
  }, [columns]);

  /**
   * Determines if a column should be disabled.
   * A column is disabled if its parent column has no selections.
   */

  const isDisabled = useCallback(
    (column: string): boolean => {
      const index = columns.indexOf(column);

      // First column is never disabled
      if (index === 0) return false;

      // If any parent column has no selection → disabled
      for (let i = 0; i < index; i++) {
        const parent = columns[i];
        const selected = filters[parent];

        if (!selected || selected.length === 0) {
          return true;
        }
      }

      return false;
    },
    [columns, filters],
  );

  const location = useLocation();
  const currentPath = location.pathname;

  /**
   * Handles selecting/deselecting a value inside a column.
   * Resets all lower levels and triggers fetching new options.
   */

  const handleChange = useCallback(
    (col: string, option: GeoClass) => {
      const columnIndex = columns.indexOf(col);
      const prevFilters = { ...filters };

      let currentSelections: GeoClass[] = Array.isArray(filters[col])
        ? filters[col]
        : filters[col]?.id
          ? [filters[col]]
          : [];

      let newSelections: GeoClass[];

      // Special case: single-select behavior for GROUP column on Cash Register page
      if (col === GeoColumns.GROUP && currentPath === CASH_REGISTER_URL) {
        const currentlySelected = currentSelections[0];
        newSelections = currentlySelected?.ID === option.ID ? [] : [option];
      } else {
        // Multi-select
        const isSelected = currentSelections.some((item) => item.ID === option.ID);
        newSelections = isSelected
          ? currentSelections.filter((item) => item.ID !== option.ID)
          : [...currentSelections, option];
      }

      const newFilters = { ...filters, [col]: newSelections };

      // Clear selections in all columns below
      for (let i = columnIndex + 1; i < columns.length; i++) {
        newFilters[columns[i]] = [];
      }

      setFilters(newFilters);

      // Prepare only IDs to avoid large objects in dispatch
      const selectedIds = newSelections.map((f) => f.ID);

      dispatch(
        fetchGeoFilters({
          [`${col}ID`]: selectedIds,
          GetAllAbove: false,
          GetAllBelow: false,
          GetUserLevel: false,
          currentColumn: col,
          currentFilters: newFilters,
          previousFilters: prevFilters,
          currentPath: currentPath,
          StartDate: startDate,
        }),
      );
    },
    [columns, filters, setFilters, dispatch, currentPath, startDate],
  );

  /**
   * Lazy-loads options when a dropdown is opened.
   */

  const handleDropdownOpen = useCallback(
    (col: string) => {
      // If the column already has options, do nothing:
      const options = allOptsFiltered[col] || [];
      if (options.length > 0) return;

      // Find the parent column:
      const colIndex = columns.indexOf(col);
      if (colIndex === 0) return;
      const parentCol = columns[colIndex - 1];

      const selected = filters[parentCol] || [];
      if (selected.length === 0) return;

      const newFilters = { ...filters };

      // Clear invalid selections in all columns below the current one:
      for (let nextColIndex = colIndex + 1; nextColIndex < columns.length; nextColIndex++) {
        const nextColumn = columns[nextColIndex];
        const validOptionsForColumn = allOptsFiltered[nextColumn];

        // If there are no valid options for this column, skip it
        if (!validOptionsForColumn || validOptionsForColumn.length === 0) continue;

        // Get currently selected options for this column
        const selectedOptions = newFilters[nextColumn] || [];

        // Keep only those selected options which are still valid
        newFilters[nextColumn] = selectedOptions.filter((selectedOption: GeoClass) =>
          validOptionsForColumn.some((validOption) => validOption.ID === selectedOption.ID),
        );
      }
      setFilters(newFilters);

      dispatch(
        fetchGeoFilters({
          [`${parentCol}ID`]: selected.map((f: GeoClass) => f.ID),
          GetAllAbove: false,
          GetAllBelow: false,
          GetUserLevel: false,
          currentColumn: col,
          currentFilters: newFilters,
          previousFilters: filters,
          StartDate: startDate,
        }),
      );
    },
    [columns, filters, allOptsFiltered, setFilters, dispatch, startDate],
  );

  /**
   * Selects or deselects all options inside a column.
   * Clears all lower-level selections.
   */

  const selectAll = useCallback(
    (col: string, options: GeoClass[]) => {
      const colIndex = columns.indexOf(col);

      // 1. Create a shallow copy of the current filters to avoid mutating state directly
      const updated = { ...filters };

      // 2. Clear selections in all columns below the current column (cascading logic)
      for (let i = colIndex + 1; i < columns.length; i++) {
        updated[columns[i]] = [];
      }

      // 3. Get the current selections for this column, handling both arrays and single object cases
      const current = updated[col];
      const currentArray = Array.isArray(current) ? current : current?.id ? [current] : [];

      // 4. Determine if all options are already selected
      const isAllSelected = currentArray.length === options.length;

      // 5. Toggle selection: if all selected, clear; otherwise, select all
      updated[col] = isAllSelected ? [] : options;

      // 6. Prepare an array of IDs only (to avoid sending full objects in fetch)
      const selectedIds: number[] = Array.isArray(updated[col])
        ? updated[col].map((f: GeoClass) => f.ID)
        : updated[col]?.id
          ? [updated[col].id]
          : [];

      // 7. Update the global filter in Redux with the new selection
      dispatch(setGlobalFilter({ key: col as keyof GlobalFiltersState, value: updated[col] }));

      // 8. Fetch dependent filter options for lower columns using only IDs
      dispatch(
        fetchGeoFilters({
          [`${col}ID`]: selectedIds,
          GetAllAbove: false,
          GetAllBelow: false,
          GetUserLevel: false,
          currentColumn: col,
          currentFilters: updated,
          previousFilters: filters,
          StartDate: startDate,
        }),
      );

      // 9. Update the local state with the new selections
      setFilters(updated);
    },
    [columns, filters, dispatch, setFilters, startDate],
  );

  /**
   * Updates the number of selected items per column.
   */

  useEffect(() => {
    const newCounts: Record<string, number> = {};

    let changed = false;

    columns.forEach((col) => {
      const selected = filters[col] || [];

      let count = 0;

      count = selected.length;

      if (isDisabled(col)) {
        count = 0;
      }

      newCounts[col] = count;

      if (numberOfSelected[col] !== count) {
        changed = true;
      }
    });

    if (changed) {
      setNumberOfSelected(newCounts);
    }
  }, [filters, isDisabled, columns, numberOfSelected]);

  return {
    handleChange,
    handleDropdownOpen,
    selectAll,
    numberOfSelected,
    filteredGeoData,
    onFilteredData,
    isDisabled,
  };
};
